import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Tooltip,
} from '@chakra-ui/react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getWorkReport } from '../../../redux/actions/Files.action';
import { allCounselorDetail } from '../../../redux/actions/Files.action';
import ReactPaginate from 'react-paginate';

export default function WorkReport() {
  const dispatch = useDispatch();
  const getFile = useSelector((state) => state.files);
  const isAdmin = localStorage.getItem('type') === 'Admin';
  const managers = getFile?.allCounselor?.data?.CounselorsData || [];

  const [filters, setFilters] = useState({
    date: moment().format('YYYY-MM-DD'),
    manageId: '',
    page: 1,
    limit: 10,
  });

  // Load manager dropdown data (Admin only — mirrors Lead page)
  useEffect(() => {
    if (isAdmin) {
      dispatch(allCounselorDetail());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    const hideBreadcrumbTitle = () => {
      const breadcrumbNav = document.querySelector(
        'nav[aria-label="breadcrumb"]',
      );
      if (!breadcrumbNav || !breadcrumbNav.parentElement) return;

      const titleLink =
        breadcrumbNav.parentElement.querySelector('a.chakra-link');
      if (
        titleLink &&
        titleLink.textContent.trim().replace(/\s+/g, '') === 'WorkReport'
      ) {
        titleLink.dataset.hiddenByWorkReport = 'true';
        titleLink.style.display = 'none';
      }
    };

    hideBreadcrumbTitle();

    const observer = new MutationObserver(hideBreadcrumbTitle);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      document
        .querySelectorAll('[data-hidden-by-work-report="true"]')
        .forEach((el) => {
          el.style.display = '';
          delete el.dataset.hiddenByWorkReport;
        });
    };
  }, []);

  // Fetch report whenever filters change
  useEffect(() => {
    dispatch(getWorkReport(filters));
  }, [dispatch, filters]);

  const handleDateChange = (e) => {
    setFilters((prev) => ({ ...prev, date: e.target.value, page: 1 }));
  };

  const handleManagerChange = (e) => {
    setFilters((prev) => ({ ...prev, manageId: e.target.value, page: 1 }));
  };

  const handlePageChange = (selected) => {
    const newPage = selected.selected + 1;
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const reportData = getFile?.getWorkReport?.data?.data?.report || {};
  const totalCount = getFile?.getWorkReport?.data?.data?.total || 0;
  const leadsList = getFile?.getWorkReport?.data?.data?.data || [];
  const pageCount = Math.ceil(totalCount / filters.limit) || 0;

  const rows = Object.entries(reportData).map(([status, count]) => ({
    status,
    count,
  }));

  const truncate = (text, maxLength = 60) => {
    if (!text) return '-';
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  return (
    <div className="mt-[100px] mb-4">
      <div className="mb-3 flex items-center justify-between mt-5">
        <h3 className="fw-bold" style={{ fontSize: '2rem' }}>
          Work Report
        </h3>
      </div>

      {/* Filters */}
      <div className="d-flex flex-wrap gap-4 mb-4">
        <div style={{ minWidth: '180px' }}>
          <label className="text-sm d-block mb-1">Date</label>
          <input
            type="date"
            value={filters.date}
            onChange={handleDateChange}
            className="w-100 outline-none"
            style={{
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              padding: '8px 12px',
            }}
          />
        </div>

        {/* Manager filter — Admin only, same as Lead page */}
        {isAdmin && (
          <div style={{ minWidth: '180px' }}>
            <label className="text-sm d-block mb-1">Manager</label>
            <select
              value={filters.manageId}
              onChange={handleManagerChange}
              className="w-100 outline-none"
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '8px 12px',
              }}
            >
              <option value="">Select Manager</option>
              <option value="no_assign">No Assign</option>
              {managers.map((manager) => (
                <option key={manager._id} value={manager._id}>
                  {manager.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <TableContainer>
        <Table variant="striped" colorScheme="blackAlpha">
          <Thead>
            <Tr>
              <Th>Lead Status</Th>
              <Th className="text-end">Count</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.length > 0 ? (
              rows.map((row) => (
                <Tr key={row.status}>
                  <Td>{row.status}</Td>
                  <Td className="text-end">{row.count}</Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={2} textAlign="center">
                  No Data Found
                </Td>
              </Tr>
            )}
            <Tr>
              <Td className="fw-bold">Total</Td>
              <Td className="text-end fw-bold">{totalCount}</Td>
            </Tr>
          </Tbody>
        </Table>
      </TableContainer>

      {/* Lead-wise detail table */}
      <div className="mt-5">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h4 className="fw-bold mb-0">Lead Details</h4>
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm">Rows per page:</span>
            <select
              value={filters.limit}
              onChange={handleRowsPerPageChange}
              className="border rounded-md focus:outline-none"
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '6px 10px',
              }}
            >
              <option value={10}>10</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* <div className="mb-2">
          Total Records:{' '}
          <span className="text-success border-b border-success">
            {totalCount}
          </span>
        </div> */}

        <TableContainer>
          <Table variant="striped" colorScheme="blackAlpha">
            <Thead>
              <Tr>
                <Th>First Name</Th>
                <Th>Lead Status</Th>
                <Th>Remark</Th>
              </Tr>
            </Thead>
            <Tbody>
              {leadsList.length > 0 ? (
                leadsList.map((lead) => (
                  <Tr key={lead._id}>
                    <Td>{lead.firstName || '-'}</Td>
                    <Td>{lead.leadStatus || '-'}</Td>
                    <Td>
                      <Tooltip
                        label={lead.remark || 'No remark'}
                        hasArrow
                        placement="top"
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                        borderRadius="6px"
                        px={3}
                        py={2}
                        maxW="320px"
                        whiteSpace="normal"
                        openDelay={200}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            cursor: 'default',
                          }}
                        >
                          {truncate(lead.remark)}
                        </span>
                      </Tooltip>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={3} textAlign="center">
                    No Data Found
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {leadsList.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <span className="text-sm text-muted">
              Showing {(filters.page - 1) * filters.limit + 1}–
              {Math.min(filters.page * filters.limit, totalCount)} of{' '}
              {totalCount}
            </span>
            <ReactPaginate
              breakLabel="..."
              nextLabel="next >"
              onPageChange={handlePageChange}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={pageCount}
              forcePage={filters.page - 1}
              previousLabel="< prev"
              renderOnZeroPageCount={null}
              className="pagination"
            />
          </div>
        )}
      </div>
    </div>
  );
}
